import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Eye, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * WhatsNewBroadcastCard (iter149)
 *
 * Admin-only card that fires the "What's New" feature-announcement email
 * to every deliverable user. Shows last-sent timestamp so we don't
 * accidentally double-blast the 36-user list.
 */
export default function WhatsNewBroadcastCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/announcements/whats-new/status");
      setStatus(data);
    } catch (e) { /* silent — first-time load */ }
  };

  useEffect(() => { loadStatus(); }, []);

  const openPreview = () => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backend}/api/admin/announcements/whats-new/preview`, "_blank");
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/announcements/whats-new/test");
      if (data.ok) {
        const dest = data.to || "your admin inbox";
        toast.success(`Test email sent to ${dest} — check your inbox.`);
      } else {
        toast.error(`Test send failed: ${data.detail?.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const sendReal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/announcements/whats-new");
      toast.success(`Blast sent · ${data.sent} delivered, ${data.failed} failed.`);
      setConfirming(false);
      loadStatus();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const lastSentTs = status?.ran_at;
  const lastSentPretty = lastSentTs
    ? new Date(lastSentTs).toLocaleString("en-GB", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <section
      className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-gradient-to-br from-white to-[#fff4ee]"
      data-testid="admin-whats-new-broadcast"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
            <span>📢</span> Community broadcast
          </div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight">
            "What's New" announcement
          </h2>
          <p className="text-xs text-[#666] mt-1 max-w-xl">
            Sends the DMs · Chat Room · Verified Prices · Featured Reel · Weekend
            Weather rundown to every camper (personalised by first name).
            Respects newsletter opt-outs.
          </p>
        </div>
        {lastSentPretty && (
          <div
            className="text-[11px] px-2 py-1 rounded-full bg-[#ecfdf5] text-[#065f46] font-semibold flex items-center gap-1 shrink-0"
            data-testid="whats-new-last-sent"
          >
            <CheckCircle2 className="w-3 h-3" />
            Last sent {lastSentPretty} · {status?.sent_count || 0} delivered
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={openPreview}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border hairline bg-white text-sm font-semibold hover:bg-[#fff4ee]"
          data-testid="whats-new-preview-btn"
        >
          <Eye className="w-4 h-4" /> Preview
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={sendTest}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border hairline bg-white text-sm font-semibold hover:bg-[#fff4ee] disabled:opacity-60"
          data-testid="whats-new-test-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send to me first
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setConfirming(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#ff4500] text-white text-sm font-black uppercase tracking-wider hover:bg-[#e63e00] disabled:opacity-60"
          data-testid="whats-new-broadcast-btn"
        >
          <Send className="w-4 h-4" /> Broadcast to everyone
        </button>
      </div>

      {confirming && (
        <div
          className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center p-4"
          onClick={() => !busy && setConfirming(false)}
          data-testid="whats-new-confirm-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 text-[#b45309]">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-bold">Send to every camper?</span>
            </div>
            <p className="text-sm text-[#444] mt-3">
              This blasts the "What's New" email to every user with a valid
              address who hasn't opted out of newsletters. There's no undo.
              {lastSentPretty && (
                <span className="block mt-2 text-[#b45309] font-semibold">
                  Heads up — you already sent this on {lastSentPretty}. Sending again
                  will re-email everyone.
                </span>
              )}
            </p>
            <div className="mt-5 flex gap-2 justify-end">
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirming(false)}
                className="px-4 py-2 rounded-full border hairline text-sm font-semibold hover:bg-[#f6f6f6]"
                data-testid="whats-new-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={sendReal}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#ff4500] text-white text-sm font-black uppercase tracking-wider disabled:opacity-60"
                data-testid="whats-new-confirm-send-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Yes, send now
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
